import { useCallback, useEffect, useRef, useState } from 'react'
import { InlineAlert } from '../../../components/InlineAlert'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { Icon } from '../../../components/icons/Icon'
import { MakerCheckerBar } from './MakerCheckerBar'
import { FixturesResultsList, type FixtureResultRow } from './FixturesResultsList'
import { DecisionGraphCanvas, type CanvasEdge, type CanvasNode } from './DecisionGraphCanvas'
import { CalcGraphPicker } from './CalcGraphPicker'
import {
  createCalcTestFixture,
  createCalculationGraph,
  runCalcFixtures,
} from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { CalcFixtureRunSummaryView, CalculationGraphView } from '../../../types/cartridgeStudio'

export interface CalcDagTabProps {
  programId: string
}

/** Design doc §4.7 — Calc DAG tab: real, validated edges (no inference needed), direct-publish
 * `MakerCheckerBar`, and an `informational`-only `FixturesResultsList` (RAID R-19 — never gates
 * `createCalculationGraph`, copy never says "publish blocked"). */
export function CalcDagTab({ programId }: CalcDagTabProps) {
  const [selectedGraph, setSelectedGraph] = useState<CalculationGraphView | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null)
  const [newGraphOpen, setNewGraphOpen] = useState(false)
  const [fixtureResults, setFixtureResults] = useState<CalcFixtureRunSummaryView | null>(null)
  const [fixturesRunning, setFixturesRunning] = useState(false)

  const runFixtures = useCallback(async () => {
    if (!selectedGraph) return
    setFixturesRunning(true)
    try {
      const summary = await runCalcFixtures(selectedGraph.id)
      setFixtureResults(summary)
    } catch {
      // Informational surface only — see FixturesResultsList's own note; a run failure here leaves
      // the prior result visible rather than blocking anything (nothing to block, ADR-0020).
    } finally {
      setFixturesRunning(false)
    }
  }, [selectedGraph])

  useEffect(() => {
    setFixtureResults(null)
    setSelectedNodeKey(null)
  }, [selectedGraph?.id])

  const nodes: CanvasNode[] = (selectedGraph?.nodes ?? []).map((n) => ({
    key: n.key,
    label: n.key,
    variant: n.type,
    subtitle:
      n.type === 'control'
        ? String((n as Record<string, unknown>).op ?? '')
        : n.type === 'cel'
          ? String((n as Record<string, unknown>).expression ?? '').slice(0, 24)
          : undefined,
  }))
  const edges: CanvasEdge[] = selectedGraph?.edges ?? []
  const selectedNode = selectedGraph?.nodes.find((n) => n.key === selectedNodeKey) ?? null

  const resultRows: FixtureResultRow[] =
    fixtureResults?.results.map((r) => ({
      fixtureId: r.fixtureId,
      label: r.fixtureId.slice(0, 8),
      expected: JSON.stringify(r.expectedOutputs),
      actual: JSON.stringify(r.actualOutputs),
      passed: r.passed,
    })) ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', gap: 'var(--iw-space-4)', alignItems: 'flex-start' }}>
        <CalcGraphPicker programId={programId} selectedId={selectedGraph?.id ?? null} onSelect={setSelectedGraph} refreshToken={refreshToken} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="iw-btn iw-btn--sm" onClick={() => setNewGraphOpen(true)}>
              <Icon name="plus" size={13} />
              New graph version
            </button>
          </div>

          <MakerCheckerBar
            artifactLabel={selectedGraph ? `CalculationGraph v${selectedGraph.graphVersion}` : 'CalculationGraph'}
            variant="direct-publish"
            onNewVersion={() => setNewGraphOpen(true)}
          />

          {selectedGraph ? (
            <>
              <DecisionGraphCanvas
                ariaLabel="Calculation graph"
                nodes={nodes}
                edges={edges}
                selectedKey={selectedNodeKey}
                onSelect={setSelectedNodeKey}
                emptyMessage="This graph has no nodes."
              />

              {selectedNode ? (
                <div style={{ border: '1px solid var(--iw-border)', borderRadius: 'var(--iw-radius)', padding: 'var(--iw-space-3)', fontFamily: 'var(--iw-font-mono)', fontSize: 'var(--iw-size-xs)' }}>
                  {JSON.stringify(selectedNode)}
                </div>
              ) : null}

              <CalcFixtureAuthor graphId={selectedGraph.id} onFixtureAdded={() => void runFixtures()} />

              <FixturesResultsList
                mode="informational"
                results={fixtureResults ? resultRows : null}
                allPassed={fixtureResults?.allPassed ?? null}
                onRun={() => void runFixtures()}
                running={fixturesRunning}
                disclosure="Fixture runs use this draft's own node/edge structure but only currently-published Parameter values — a sibling draft Parameter's new rate is not reflected until it publishes."
              />
            </>
          ) : null}
        </div>
      </div>

      {newGraphOpen ? (
        <NewCalcGraphDialog
          programId={programId}
          onClose={() => setNewGraphOpen(false)}
          onCreated={(created) => {
            setNewGraphOpen(false)
            setSelectedGraph(created)
            setRefreshToken((t) => t + 1)
          }}
        />
      ) : null}
    </div>
  )
}

function NewCalcGraphDialog({
  programId,
  onClose,
  onCreated,
}: {
  programId: string
  onClose: () => void
  onCreated: (graph: CalculationGraphView) => void
}) {
  const [nodes, setNodes] = useState('[{"key":"credit","type":"output","source":"qualifiedSpend"}]')
  const [edges, setEdges] = useState('[]')
  const [graphVersion, setGraphVersion] = useState('1')
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [validTo, setValidTo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nodesRef = useRef<HTMLTextAreaElement>(null)

  let parsedNodes: unknown = null
  let parsedEdges: unknown = null
  let jsonError = false
  try {
    parsedNodes = JSON.parse(nodes)
    parsedEdges = JSON.parse(edges)
  } catch {
    jsonError = true
  }
  const parsedVersion = Number(graphVersion)
  const isValid = !jsonError && Number.isInteger(parsedVersion) && parsedVersion >= 1 && validFrom !== ''

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await createCalculationGraph(programId, {
        nodes: parsedNodes as never,
        edges: parsedEdges as never,
        graphVersion: parsedVersion,
        validFrom,
        validTo: validTo || null,
      })
      onCreated(created)
    } catch (err) {
      const violations = err instanceof ApiError ? err.problem.violations : undefined
      setError(
        violations && violations.length > 0
          ? violations.map((v) => v.message ?? v.code).join('; ')
          : err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not create this graph version.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="new-calc-graph-title"
      title="New graph version"
      onClose={onClose}
      initialFocusRef={nodesRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create (publishes immediately — ADR-0020)'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        <InlineAlert severity="info">
          ADR-0020 — this creates the next graph version immediately effective. There is no draft/review/approve step.
        </InlineAlert>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-calc-nodes">
            Nodes (JSON array — six real types: input|parameter|operator|control|cel|output)
          </label>
          <textarea id="new-calc-nodes" ref={nodesRef} className="iw-input" rows={4} style={{ fontFamily: 'var(--iw-font-mono)' }} value={nodes} onChange={(e) => setNodes(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-calc-edges">
            Edges (JSON array of {'{from,to}'})
          </label>
          <textarea id="new-calc-edges" className="iw-input" rows={2} style={{ fontFamily: 'var(--iw-font-mono)' }} value={edges} onChange={(e) => setEdges(e.target.value)} />
        </div>
        {jsonError ? <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>Nodes/Edges must be valid JSON.</span> : null}
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-calc-version">
            Graph version
          </label>
          <input id="new-calc-version" className="iw-input" type="number" min={1} value={graphVersion} onChange={(e) => setGraphVersion(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-calc-valid-from">
            Valid from
          </label>
          <input id="new-calc-valid-from" className="iw-input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-calc-valid-to">
            Valid to (optional)
          </label>
          <input id="new-calc-valid-to" className="iw-input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
        </div>
      </form>
    </FocusTrapDialog>
  )
}

function CalcFixtureAuthor({ graphId, onFixtureAdded }: { graphId: string; onFixtureAdded: () => void }) {
  const [ruleSetId, setRuleSetId] = useState('')
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10))
  const [inputBindings, setInputBindings] = useState('{}')
  const [expectedOutputs, setExpectedOutputs] = useState('{}')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  let parsedInputs: unknown = null
  let parsedExpected: unknown = null
  let jsonError = false
  try {
    parsedInputs = JSON.parse(inputBindings)
    parsedExpected = JSON.parse(expectedOutputs)
  } catch {
    jsonError = true
  }
  const isValid = ruleSetId.trim() !== '' && asOf !== '' && !jsonError

  async function handleAdd() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await createCalcTestFixture(graphId, { ruleSetId: ruleSetId.trim(), asOf, inputBindings: parsedInputs, expectedOutputs: parsedExpected })
      onFixtureAdded()
    } catch (err) {
      const violations = err instanceof ApiError ? err.problem.violations : undefined
      setError(
        violations && violations.length > 0
          ? violations.map((v) => v.message ?? v.code).join('; ')
          : err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not add this fixture.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ border: '1px solid var(--iw-border)', borderRadius: 'var(--iw-radius)', padding: 'var(--iw-space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      <span style={{ fontWeight: 'var(--iw-weight-medium)' as never }}>Author a fixture</span>
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="calc-fixture-ruleset">
          RuleSet id
        </label>
        <input
          id="calc-fixture-ruleset"
          className="iw-input"
          value={ruleSetId}
          onChange={(e) => setRuleSetId(e.target.value)}
          placeholder="Paste the RuleSet id from the Parameters tab"
        />
      </div>
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="calc-fixture-asof">
          As of
        </label>
        <input id="calc-fixture-asof" className="iw-input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </div>
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="calc-fixture-inputs">
          Input bindings (JSON — string keys to string decimal literals)
        </label>
        <textarea id="calc-fixture-inputs" className="iw-input" rows={2} style={{ fontFamily: 'var(--iw-font-mono)' }} value={inputBindings} onChange={(e) => setInputBindings(e.target.value)} placeholder='{"qualifiedSpend":"2000000"}' />
      </div>
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="calc-fixture-outputs">
          Expected outputs (JSON)
        </label>
        <textarea id="calc-fixture-outputs" className="iw-input" rows={2} style={{ fontFamily: 'var(--iw-font-mono)' }} value={expectedOutputs} onChange={(e) => setExpectedOutputs(e.target.value)} placeholder='{"credit":"600000.00"}' />
      </div>
      {jsonError ? <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>Must be valid JSON.</span> : null}
      <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleAdd()} disabled={!isValid || submitting}>
        <Icon name="plus" size={13} />
        Add fixture
      </button>
    </div>
  )
}
